import * as THREE from 'three';
import TWEEN from 'three/addons/libs/tween.module.js';

export function getTransition({ renderer, sceneA, sceneB }) {
  const transitionParams = {
    transition: 0,
    texture: 0,
    cycle: true,
    animate: true,
    threshold: 0.1,
    useTexture: true,
  };

  const scene = new THREE.Scene();
  let width = window.innerWidth;
  let height = window.innerHeight;

  const camera = new THREE.OrthographicCamera(
    width / -2,
    width / 2,
    height / 2,
    height / -2,
    -10,
    10
  );

  // Load transition mask textures from public directory
  const baseUrl = import.meta.env.BASE_URL || '/';
  const textureFiles = [
    `${baseUrl}images/transition0.png`,
    `${baseUrl}images/transition1.png`,
    `${baseUrl}images/transition2.png`,
  ];

  const loader = new THREE.TextureLoader();
  const textures = textureFiles.map((path) => {
    const tex = loader.load(path);
    tex.wrapS = THREE.RepeatWrapping;
    tex.wrapT = THREE.RepeatWrapping;
    return tex;
  });

  const material = new THREE.ShaderMaterial({
    uniforms: {
      tDiffuse1: { value: sceneA.fbo.texture },
      tDiffuse2: { value: sceneB.fbo.texture },
      mixRatio: { value: 0.0 },
      threshold: { value: transitionParams.threshold },
      useTexture: { value: 1 },
      tMixTexture: { value: textures[0] },
    },
    vertexShader: `
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      uniform float mixRatio;
      uniform sampler2D tDiffuse1;
      uniform sampler2D tDiffuse2;
      uniform sampler2D tMixTexture;
      uniform int useTexture;
      uniform float threshold;
      varying vec2 vUv;

      void main() {
        vec4 texel1 = texture2D(tDiffuse1, vUv);
        vec4 texel2 = texture2D(tDiffuse2, vUv);

        if (useTexture == 1) {
          vec4 transitionTexel = texture2D(tMixTexture, vUv);
          float r = mixRatio * (1.0 + threshold * 2.0) - threshold;
          float mixf = clamp((transitionTexel.r - r) * (1.0 / threshold), 0.0, 1.0);
          gl_FragColor = mix(texel1, texel2, mixf);
        } else {
          gl_FragColor = mix(texel2, texel1, mixRatio);
        }
      }
    `,
  });

  const geometry = new THREE.PlaneGeometry(width, height);
  const mesh = new THREE.Mesh(geometry, material);
  scene.add(mesh);

  const tween = new TWEEN.Tween(transitionParams)
    .to({ transition: 1 }, 4000)
    .repeat(Infinity)
    .delay(8000)
    .yoyo(true)
    .start();

  let needsTextureChange = true;

  const nextTexture = () => {
    if (textures.length > 0) {
      transitionParams.texture = (transitionParams.texture + 1) % textures.length;
      material.uniforms.tMixTexture.value = textures[transitionParams.texture];
    }
  };

  const render = (delta) => {
    if (transitionParams.animate) {
      TWEEN.update();

      // Cycle the transition texture after each full transition phase
      if (transitionParams.cycle && textures.length > 0) {
        if (transitionParams.transition === 0 || transitionParams.transition === 1) {
          if (needsTextureChange) {
            nextTexture();
            needsTextureChange = false;
          }
        } else {
          needsTextureChange = true;
        }
      } else {
        needsTextureChange = true;
      }
    }

    material.uniforms.mixRatio.value = transitionParams.transition;
    material.uniforms.threshold.value = transitionParams.threshold;
    material.uniforms.useTexture.value = transitionParams.useTexture ? 1 : 0;

    // Optimize rendering: only render both scenes to textures during active transition
    if (transitionParams.transition === 0) {
      sceneA.update(delta);
      sceneB.render(delta, false);
    } else if (transitionParams.transition === 1) {
      sceneA.render(delta, false);
      sceneB.update(delta);
    } else {
      sceneA.render(delta, true);
      sceneB.render(delta, true);

      renderer.setRenderTarget(null);
      renderer.render(scene, camera);
    }
  };

  const onResize = (newWidth, newHeight) => {
    width = newWidth;
    height = newHeight;

    camera.left = width / -2;
    camera.right = width / 2;
    camera.top = height / 2;
    camera.bottom = height / -2;
    camera.updateProjectionMatrix();

    mesh.geometry.dispose();
    mesh.geometry = new THREE.PlaneGeometry(width, height);

    sceneA.onResize(width, height);
    sceneB.onResize(width, height);
  };

  const dispose = () => {
    tween.stop();
    geometry.dispose();
    material.dispose();
    textures.forEach((tex) => tex.dispose());
  };

  return {
    params: transitionParams,
    nextTexture,
    render,
    onResize,
    dispose,
  };
}
